/**
 * Cuts a room's four walls out of its rectangle, around whatever openings it has.
 *
 * Split out of `buildGeometry` once windows arrived: a doorway leaves wall above it, a
 * window leaves wall both above *and* below, and between the two the interval-cutting
 * had grown into the majority of that file. Everything about where a wall stops and an
 * opening starts now lives here.
 *
 * Walls are per room and two-sided: each room lines the inside of its own boundary, so
 * a boundary shared by two rooms carries one wall face per room. That is what lets every
 * room have its own wall colour and pattern, at the cost of a few extra boxes.
 *
 * Pure and headless, like the rest of `src/game/`.
 */

import type { DoorFace, RoomWall, WindowFace } from './buildGeometry';
import type {
  Box,
  DoorSpec,
  GroundPoint,
  Rect,
  RoomSpec,
  WallPattern,
  WallSide,
  WindowSpec,
} from './types';

/** Thickness of one room's wall face. A shared boundary is twice this. */
export const WALL_THICKNESS = 0.12;

type Interval = [number, number];

/** Cut openings out of a span, keeping what remains. */
export function cutIntervals(span: Interval, openings: Interval[]): Interval[] {
  let remaining: Interval[] = [span];
  for (const [cutFrom, cutTo] of openings) {
    remaining = remaining.flatMap(([from, to]): Interval[] => {
      if (cutTo <= from || cutFrom >= to) return [[from, to]];
      const pieces: Interval[] = [];
      if (cutFrom > from) pieces.push([from, cutFrom]);
      if (cutTo < to) pieces.push([cutTo, to]);
      return pieces;
    });
  }
  return remaining.filter(([from, to]) => to - from > 1e-6);
}

type Edge = {
  /** Axis the edge's line is perpendicular to. */
  axis: 'x' | 'z';
  line: number;
  /** Where the wall's centre sits — inset half a thickness into the room. */
  inset: number;
  span: Interval;
  /** Which of the room's four walls this is, for matching authored windows. */
  side: WallSide;
};

function roomEdges(rect: Rect): Edge[] {
  const half = WALL_THICKNESS / 2;
  const alongZ: Interval = [rect.z, rect.z + rect.depth];
  const alongX: Interval = [rect.x, rect.x + rect.width];

  return [
    { side: 'west', axis: 'x', line: rect.x, inset: rect.x + half, span: alongZ },
    {
      side: 'east',
      axis: 'x',
      line: rect.x + rect.width,
      inset: rect.x + rect.width - half,
      span: alongZ,
    },
    { side: 'north', axis: 'z', line: rect.z, inset: rect.z + half, span: alongX },
    {
      side: 'south',
      axis: 'z',
      line: rect.z + rect.depth,
      inset: rect.z + rect.depth - half,
      span: alongX,
    },
  ];
}

/**
 * Which way the room lies from this edge. `inset` is pulled half a wall thickness off
 * the boundary line towards the room's middle, so their difference gives the sign.
 */
function edgeInward(edge: Edge): GroundPoint {
  const sign = Math.sign(edge.inset - edge.line);
  return edge.axis === 'x' ? { x: sign, z: 0 } : { x: 0, z: sign };
}

function segmentBox(edge: Edge, segment: Interval, bottom: number, top: number): Box {
  const [from, to] = segment;
  const along = { center: (from + to) / 2, size: to - from };
  const center = { y: (bottom + top) / 2 };
  const size = { y: top - bottom };

  return edge.axis === 'x'
    ? {
        center: { x: edge.inset, ...center, z: along.center },
        size: { x: WALL_THICKNESS, ...size, z: along.size },
      }
    : {
        center: { x: along.center, ...center, z: edge.inset },
        size: { x: along.size, ...size, z: WALL_THICKNESS },
      };
}

function doorsOnEdge(edge: Edge, doors: readonly DoorSpec[], roomId: string): DoorSpec[] {
  return doors.filter((door) => {
    if (!door.betweenRooms.includes(roomId)) return false;
    const line = edge.axis === 'x' ? door.center.x : door.center.z;
    return Math.abs(line - edge.line) < 1e-4;
  });
}

function doorOpening(edge: Edge, door: DoorSpec): Interval {
  const along = edge.axis === 'x' ? door.center.z : door.center.x;
  return [along - door.width / 2, along + door.width / 2];
}

/** Where a window sits along its edge, in world units. */
function windowOpening(edge: Edge, window: WindowSpec): Interval {
  const centre = edge.span[0] + window.offset;
  return [centre - window.width / 2, centre + window.width / 2];
}

/** A point on the wall's centre plane, at the given height along the edge. */
function facePoint(edge: Edge, along: number, y: number) {
  return edge.axis === 'x'
    ? { x: edge.inset, y, z: along }
    : { x: along, y, z: edge.inset };
}

export type BuiltWalls = {
  walls: RoomWall[];
  doorFaces: DoorFace[];
  windowFaces: WindowFace[];
};

export function buildWalls(
  rooms: readonly RoomSpec[],
  doors: readonly DoorSpec[],
  ceilingHeight: number,
  styleOf: (room: RoomSpec) => { color: string; pattern: WallPattern },
): BuiltWalls {
  const walls: RoomWall[] = [];
  const doorFaces: DoorFace[] = [];
  const windowFaces: WindowFace[] = [];

  for (const room of rooms) {
    const { color, pattern } = styleOf(room);

    for (const edge of roomEdges(room.rect)) {
      const edgeDoors = doorsOnEdge(edge, doors, room.id);
      const edgeWindows = (room.windows ?? []).filter((window) => window.wall === edge.side);
      const inward = edgeInward(edge);
      const piece = (box: Box, isFullHeight: boolean): RoomWall => ({
        roomId: room.id,
        box,
        color,
        pattern,
        isFullHeight,
        inward,
      });

      const openings = [
        ...edgeDoors.map((door) => doorOpening(edge, door)),
        ...edgeWindows.map((window) => windowOpening(edge, window)),
      ];
      for (const segment of cutIntervals(edge.span, openings)) {
        walls.push(piece(segmentBox(edge, segment, 0, ceilingHeight), true));
      }

      for (const door of edgeDoors) {
        const opening = doorOpening(edge, door);
        doorFaces.push({
          doorId: door.id,
          roomId: room.id,
          center: facePoint(edge, (opening[0] + opening[1]) / 2, door.height / 2),
          width: door.width,
          height: door.height,
          inward,
        });

        // A doorway reaching the ceiling has no wall left above it to fill.
        if (door.height >= ceilingHeight - 1e-6) continue;
        walls.push(piece(segmentBox(edge, opening, door.height, ceilingHeight), false));
      }

      // A window leaves wall both below it and above it — an apron under the sill and a
      // header over the head — where a doorway only leaves the latter.
      for (const window of edgeWindows) {
        const opening = windowOpening(edge, window);
        windowFaces.push({
          windowId: window.id,
          roomId: room.id,
          center: facePoint(edge, (opening[0] + opening[1]) / 2, (window.sill + window.head) / 2),
          width: window.width,
          sill: window.sill,
          head: window.head,
          inward,
        });

        for (const [bottom, top] of [
          [0, window.sill],
          [window.head, ceilingHeight],
        ] as const) {
          if (top - bottom <= 1e-6) continue;
          walls.push(piece(segmentBox(edge, opening, bottom, top), false));
        }
      }
    }
  }

  return { walls, doorFaces, windowFaces };
}
